import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.1 Tier 4 — CRUD UI for BudgetFormulaColumn. The formula editor
// offers slug-chip insertion, a live preview (server-side Dentaku eval
// via /formula_columns/preview), and inline error display. Formulas are
// never evaluated client-side — Dentaku runs on the server so the
// sandbox rules are authoritative.
export default class extends Controller {
  static targets = [
    "list", "empty", "addButton",
    "editorModal", "editorTitle",
    "nameInput", "formatInput", "formulaInput", "slugPreview", "slugChips",
    "sampleInput", "previewResult", "previewError",
    "deleteModal", "deleteModalLabel"
  ]

  static values = {
    projectId: Number,
    budgetId: Number,
    max: Number
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._editingId = null
    this._pendingDeleteId = null
    this._debounceTimer = null
    this._refreshEmptyState()
    this._refreshAddButton()
    this._showPendingFlash()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("budget_flash")
    if (!raw) return
    sessionStorage.removeItem("budget_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }

  stopPropagation(event) { event.stopPropagation() }

  showEditor() {
    if (this._countRows() >= this.maxValue) {
      this._showToast(`A budget can have at most ${this.maxValue} formula columns`, "error")
      return
    }
    this._editingId = null
    this.editorTitleTarget.textContent = "Add Formula Column"
    this.nameInputTarget.value = ""
    this.formatInputTarget.value = "currency"
    this.formulaInputTarget.value = ""
    this.slugPreviewTarget.textContent = "—"
    this._clearPreview()
    this._show(this.editorModalTarget)
    this.nameInputTarget.focus()
  }

  editColumn(event) {
    const id = parseInt(event.currentTarget.dataset.columnIdParam, 10)
    const row = this.element.querySelector(`[data-column-id="${id}"]`)
    if (!row) return
    this._editingId = id
    this.editorTitleTarget.textContent = "Edit Formula Column"
    this.nameInputTarget.value = row.dataset.columnName || ""
    this.formatInputTarget.value = row.dataset.columnFormat || "currency"
    this.formulaInputTarget.value = row.dataset.columnFormula || ""
    this.slugPreviewTarget.textContent = this._slugify(this.nameInputTarget.value) || "—"
    this._clearPreview()
    // Hide the chip for this column so a user can't reference the column in its own formula.
    this.slugChipsTarget.querySelectorAll("[data-column-id]").forEach(chip => {
      chip.style.display = (parseInt(chip.dataset.columnId, 10) === id) ? "none" : ""
    })
    this._show(this.editorModalTarget)
  }

  hideEditor() { this._hide(this.editorModalTarget) }

  onFormulaInput() {
    this.slugPreviewTarget.textContent = this._slugify(this.nameInputTarget.value) || "—"
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this.runPreview(), 400)
  }

  insertSlug(event) {
    const slug = event.currentTarget.dataset.slugParam
    if (!slug) return
    const ta = this.formulaInputTarget
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const before = ta.value.slice(0, start)
    const after = ta.value.slice(end)
    const insertion = (before.length > 0 && !/[\s(+\-*/,]$/.test(before)) ? ` ${slug}` : slug
    ta.value = before + insertion + after
    const caret = before.length + insertion.length
    ta.setSelectionRange(caret, caret)
    ta.focus()
    this.onFormulaInput()
  }

  async runPreview() {
    const formula = this.formulaInputTarget.value.trim()
    if (formula.length === 0) {
      this.previewResultTarget.textContent = "—"
      this._hidePreviewError()
      return
    }
    const sample = {}
    this.sampleInputTargets.forEach(input => {
      sample[input.dataset.sampleSlug] = parseFloat(input.value) || 0
    })

    const payload = {
      formula: formula,
      format: this.formatInputTarget.value,
      sample: sample,
      column_id: this._editingId
    }
    const resp = await this._ajaxJSON(
      "POST",
      `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/formula_columns/preview`,
      payload
    )
    if (resp.ok && resp.body?.ok) {
      this.previewResultTarget.textContent = resp.body.formatted || "—"
      this._hidePreviewError()
    } else {
      this.previewResultTarget.textContent = "#ERR"
      this._showPreviewError(resp.body?.error || "Formula could not be evaluated")
    }
  }

  async save() {
    const name = this.nameInputTarget.value.trim()
    const formula = this.formulaInputTarget.value.trim()
    if (!name) { this._showToast("Name is required", "error"); return }
    if (!formula) { this._showToast("Formula is required", "error"); return }

    const payload = {
      budget_formula_column: {
        name: name,
        formula: formula,
        format: this.formatInputTarget.value
      }
    }

    let resp
    if (this._editingId) {
      resp = await this._ajaxJSON(
        "PATCH",
        `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/formula_columns/${this._editingId}`,
        payload
      )
    } else {
      resp = await this._ajaxJSON(
        "POST",
        `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/formula_columns`,
        payload
      )
    }

    if (resp.ok) {
      const event = this._editingId ? "Formula Column Edited" : "Formula Column Defined"
      this._track(event, { budget_formula_column_id: resp.body.id, format: resp.body.format })
      this._hide(this.editorModalTarget)
      this._flashAndReload(this._editingId ? "Formula column updated" : "Formula column added")
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to save formula column", "error")
    }
  }

  deleteColumn(event) {
    const { columnIdParam, columnNameParam } = event.currentTarget.dataset
    this._pendingDeleteId = columnIdParam
    this.deleteModalLabelTarget.textContent = columnNameParam ? `"${columnNameParam}"` : ""
    this._show(this.deleteModalTarget)
  }

  hideDeleteModal() {
    this._hide(this.deleteModalTarget)
    this._pendingDeleteId = null
  }

  async confirmDelete() {
    if (!this._pendingDeleteId) return
    const resp = await this._ajaxJSON(
      "DELETE",
      `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/formula_columns/${this._pendingDeleteId}`
    )
    this._hide(this.deleteModalTarget)
    this._pendingDeleteId = null

    if (resp.ok) {
      this._flashAndReload("Formula column deleted")
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to delete formula column", "error")
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _slugify(name) {
    const base = String(name || "").toLowerCase().trim()
      .replace(/[^a-z0-9_]+/g, "_")
      .replace(/^_+|_+$/g, "")
    const reserved = ["original_amount", "approved_amount", "paid_to_date",
                      "approved_changes", "balance", "variance_pct"]
    if (!base || reserved.includes(base)) return `col_${base}`
    return base
  }

  _countRows() {
    return this.listTarget.querySelectorAll("[data-column-id]").length
  }

  _refreshEmptyState() {
    if (!this.hasEmptyTarget) return
    const has = this._countRows() > 0
    this.emptyTarget.classList.toggle("hidden", has)
  }

  _refreshAddButton() {
    if (!this.hasAddButtonTarget) return
    this.addButtonTarget.disabled = this._countRows() >= this.maxValue
  }

  _clearPreview() {
    this.previewResultTarget.textContent = "—"
    this._hidePreviewError()
  }

  _showPreviewError(message) {
    this.previewErrorTarget.textContent = message
    this.previewErrorTarget.classList.remove("hidden")
  }

  _hidePreviewError() {
    this.previewErrorTarget.textContent = ""
    this.previewErrorTarget.classList.add("hidden")
  }

  async _ajaxJSON(method, url, payload) {
    const options = {
      method,
      headers: {
        "X-CSRF-Token": this._csrfToken,
        "Accept": "application/json"
      }
    }
    if (payload !== undefined && method !== "GET") {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }

    const resp = await fetch(url, options)
    let body = null
    try {
      if (resp.headers.get("content-type")?.includes("application/json")) {
        body = await resp.json()
      }
    } catch { /* ignore */ }
    return { ok: resp.ok, status: resp.status, body }
  }

  _show(el) { el.classList.remove("hidden") }
  _hide(el) { el.classList.add("hidden") }

  _track(event, props = {}) {
    try {
      mixpanelTrack(event, {
        project_id: this.projectIdValue,
        budget_id: this.budgetIdValue,
        ...props
      })
    } catch { /* mixpanel optional */ }
  }

  _flashAndReload(message) {
    sessionStorage.setItem("budget_flash", JSON.stringify({ message, type: "success" }))
    window.location.reload()
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
